import React, { useState } from 'react';
import { Baby, BookOpen, Languages, Globe, Calculator, Lightbulb, GraduationCap, Award, ArrowRight } from 'lucide-react';
import { programs, images } from '../data/mock';

const iconMap = {
  Baby, BookOpen, Languages, Globe, Calculator, Lightbulb, GraduationCap, Award
};

const Programs = () => {
  const [hoveredId, setHoveredId] = useState(null);

  const colors = [
    'from-blue-500 to-blue-600',
    'from-orange-500 to-orange-600',
    'from-emerald-500 to-emerald-600',
    'from-purple-500 to-purple-600',
    'from-rose-500 to-rose-600',
    'from-cyan-500 to-cyan-600',
    'from-amber-500 to-amber-600',
    'from-indigo-500 to-indigo-600'
  ];

  return (
    <section id="program" className="py-24 bg-white relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-50 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-50 rounded-full translate-y-1/2 -translate-x-1/2" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
            <BookOpen className="w-4 h-4" />
            Program Pembelajaran
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Program <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-700">Unggulan</span> Kami
          </h2>
          <p className="text-lg text-gray-600">
            Berbagai program pembelajaran yang dirancang khusus untuk membantu siswa mencapai potensi terbaik mereka
          </p>
        </div>

        {/* Programs Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {programs.map((program, idx) => {
            const IconComponent = iconMap[program.icon];
            return (
              <div
                key={program.id}
                className="group relative bg-white rounded-2xl p-6 shadow-lg shadow-gray-100 border border-gray-100 hover:shadow-xl hover:shadow-gray-200/50 transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                onMouseEnter={() => setHoveredId(program.id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                {/* Icon */}
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${colors[idx]} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  {IconComponent && <IconComponent className="w-7 h-7 text-white" />}
                </div>

                {/* Content */}
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                  {program.name}
                </h3>
                <p className="text-gray-500 text-sm mb-4">
                  {program.description}
                </p>

                {/* Learn More Link */}
                <div className="flex items-center gap-1 text-blue-600 text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span>Pelajari lebih lanjut</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>

                {/* Hover Gradient Border */}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${colors[idx]} opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-sm`} />
              </div>
            );
          })}
        </div>

        {/* Featured Image Section */}
        <div className="mt-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src={images.classroom}
                alt="Suasana Belajar di Genius" 
                className="w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-900/40 to-transparent" />
            </div>
            {/* Decorative */}
            <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-orange-100 rounded-3xl -z-10" />
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-blue-100 rounded-2xl -z-10" />
          </div>

          <div className="space-y-6">
            <h3 className="text-3xl font-bold text-gray-900">
              Metode Pembelajaran <span className="text-orange-500">Interaktif</span>
            </h3>
            <p className="text-gray-600 text-lg leading-relaxed">
              Di Genius Education Centre, kami menggunakan pendekatan pembelajaran yang menyenangkan dan efektif. 
              Setiap siswa mendapat perhatian khusus sesuai dengan gaya belajar mereka.
            </p>
            <ul className="space-y-4">
              {['Kurikulum terupdate', 'Praktik langsung', 'Evaluasi berkala', 'Konsultasi gratis'].map((item, idx) => (
                <li key={idx} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                    <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-700 font-medium">{item}</span>
                </li>
              ))}
            </ul>
            <a 
              href="#kontak"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300 hover:scale-105"
            >
              Mulai Belajar Sekarang
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Programs;
